// src/components/pages/risk/VaR.js
import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from 'react-oidc-context';
import TickerRow from '../../TickerRow/TickerRow';
import Plot from "react-plotly.js";
import styles from "./style/VaR.module.css";
import commonStyles from "../../../styles/Common.module.css";
import { NumericFormat } from "react-number-format";
import { useTranslation } from "react-i18next";
import DOMPurify from 'dompurify';
import { ToastContainer, toast } from 'react-toastify';
import DataPeriodAlert from "../backtest/partials/DataPeriodAlert";
import NewPortfolioModal from '../portfolios/modal/NewPortfolioModal.js';
import { usePortfolioActions } from '../backtest/hooks/usePortfolioActions';
import ChallengeCta from "../../partials/ChallengeCta/ChallengeCta";

// =================================================================
// 1. 분리된 컴포넌트 외부 헬퍼 함수
// =================================================================
import { 
  getZValue, 
  getCurrencySymbol, 
  getInitialStateFromURL, 
  getHistogramData 
} from './partials/VaRUtils';


// =================================================================
// 2. Risk (VaR) 컴포넌트
// =================================================================
function Risk() {
  
  // --- 2.1 Hooks (React, Libs) ---
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const resultsRef = useRef(null);

  // --- 2.2 초기 상태 및 예시 데이터 ---
  const initialState = getInitialStateFromURL(location.search);
  const exampleTickers = [
    { ticker: "AAPL.US", weight: "40" },
    { ticker: "MSFT.US", weight: "30" },
    { ticker: "GOOG.US", weight: "30" }
  ];

  // --- 2.3 State Management ---
  
  // UI 상태
  const [isLoading, setIsLoading] = useState(false); 
  const [message, setMessage] = useState("");
  const [exampleLoaded, setExampleLoaded] = useState(false);
  
  // 입력 설정 상태
  const [rows, setRows] = useState(initialState.rows);
  const [totalInvestmentInput, setTotalInvestmentInput] = useState(initialState.totalInvestmentInput);
  const [backtestPeriod, setBacktestPeriod] = useState(initialState.period);
  const [configConfidence, setConfigConfidence] = useState(initialState.confidence);
  const [currency, setCurrency] = useState(initialState.currency);

  // 계산 및 결과 상태
  const [currentWeightSum, setCurrentWeightSum] = useState(0);
  const [totalInvestment, setTotalInvestment] = useState(Number(initialState.totalInvestmentInput));
  const [usedConfidence, setUsedConfidence] = useState(initialState.confidence);
  
  // VaR 결과 (백엔드 응답 전체)
  const [varResults, setVarResults] = useState(null); 
  // 선택된 결과 기간 (Daily, Weekly, Monthly)
  const [selectedPeriod, setSelectedPeriod] = useState('Daily'); 
  const [dataPeriodInfo, setDataPeriodInfo] = useState(null);

  // 유효한 행 필터링 (상단 정의)
  const validRows = rows.filter(row => row.ticker && Number(row.weight) > 0);

  // ✨ [추가] What-if 비중 변화 입력 상태 관리
  // key: ticker, value: 부호가 포함된 숫자 (예: -5, 10)
  const [whatIfChange, setWhatIfChange] = useState({}); 

  // ✨ [추가] What-if 입력 핸들러 (값 변경 및 부호 토글 처리)
  const handleWhatIfChange = (ticker, absValue, isNegative, currentWeight) => {
      let val = Number(absValue);
      
      // 입력값 제한 (최대 100%)
      if (val > 100) val = 100;

      // 음수 모드라면 값을 음수로 변환
      if (isNegative) {
          val = -val;
          // 유효성 검사: 현재 비중보다 더 많이 줄이려는지 체크
          if (Math.abs(val) > currentWeight) {
              val = -currentWeight; // 최대 축소 가능량으로 고정
          }
      }

      setWhatIfChange(prev => ({ ...prev, [ticker]: val }));
  };

  // --- 2.4 Custom Hooks (usePortfolioActions) ---
  const setup = {
    rows,
    setRows,
    validRows,
    totalWeight: currentWeightSum,
    message,
    setMessage,
  };
  const actions = usePortfolioActions(auth, setup, t, navigate);

  // --- 2.5 컴포넌트 내부 헬퍼 함수 ---
  
  /**
   * varResults에서 현재 선택된 기간(Daily/Weekly/Monthly)의 데이터를 추출합니다.
   */
  const getPeriodData = (period) => {
      if (!varResults) return {
          portfolioStd: null,
          varValue: null,
          historicalVaR: null,
          portfolioReturns: [],
          commonPeriod: { start: "", end: "", count: 0},
          marginalVaR: null,       
          riskContribution: null,  
      };
      
      const suffix = period; 
      
      return {
          portfolioStd: varResults[`portfolioStd_${suffix}`] || null,
          varValue: varResults[`VaR_${suffix}`] || null,
          historicalVaR: varResults[`historicalVaR_${suffix}`] || null,
          portfolioReturns: varResults[`portfolioReturns_${suffix}`] || [],
          marginalVaR: varResults[`marginalVaR_${suffix}`] || null,
          riskContribution: varResults[`riskContribution_${suffix}`] || null,

          commonPeriod: {
              start: varResults.commonPeriod[`start_${suffix}`] || "",
              end: varResults.commonPeriod[`end_${suffix}`] || "",
              count: varResults.commonPeriod[`count_${suffix}`] || 0,
          },
      };
  };

  /**
   * 숫자 포맷팅 (toLocaleString)
   */
  const formatNumber = (value) => {
    if (!value || isNaN(value)) return "-";
    return Math.round(value).toLocaleString(); 
  };

  /**
   * 총 비중 합계에 따른 CSS 클래스 반환
   */
  const getWeightSumColor = (sum) => {
    if (Math.abs(sum - 100) < 0.01) {
      return styles.greenText;
    } else if (sum > 100) {
      return styles.redText;
    } else {
      return styles.orangeText;
    }
  };


  // --- 2.6 Side Effects (useEffect) ---

  useEffect(() => {
          document.title = 'QuanPort-Risk Simulation';
    }, [t]);

  useEffect(() => {
    if (initialState.isReady) {
        const stocksArr = initialState.rows.map(row => row.ticker);
        const manualWeights = initialState.rows.reduce((acc, row) => {
            if (row.ticker && row.weight) {
                acc[row.ticker.toUpperCase()] = row.weight;
            }
            return acc;
        }, {});
        
        localStorage.setItem("myStocks", JSON.stringify(stocksArr));
        localStorage.setItem("optimizedPortfolioWeights", JSON.stringify(manualWeights));
        
        const timer = setTimeout(() => {
            calculatePortfolioMetrics(true); 
        }, 50);

        return () => clearTimeout(timer);
    }
  }, []); 

  useEffect(() => {
    if (varResults && resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [varResults]);

  useEffect(() => {
      if (exampleLoaded && rows.length > 0 && rows[0].ticker) {
          setExampleLoaded(false); 
          calculatePortfolioMetrics(false); 
      }
  }, [rows, exampleLoaded]); 
  
  useEffect(() => {
    const tickersToStore = rows.map(row => row.ticker).filter(ticker => ticker);
    localStorage.setItem("myStocks", JSON.stringify(tickersToStore));

    const sum = rows.reduce((acc, row) => acc + Number(row.weight || 0), 0);
    setCurrentWeightSum(sum);
    
    setVarResults(null);
    // What-if 상태도 입력 변경 시 초기화하는 것이 좋음
    setWhatIfChange({});

  }, [rows, backtestPeriod, configConfidence]);


  // --- 2.7 Event Handlers ---

  const handleLoadPortfolioCustom = (portfolio) => {
    actions.handleLoadPortfolio(portfolio); 
  };

  const handleAddRow = () => {
    setRows(prevRows => [...prevRows, { ticker: "", weight: "" }]);
  };

  const handleChange = (index, field, value) => {
    setRows(prevRows => prevRows.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleRemoveRow = (index) => {
    setRows(prevRows => prevRows.filter((_, i) => i !== index));
  };

  const handleLoadExample = () => {
    toast.info(t('loadExampleDisclaimer'));
    setRows(exampleTickers);
    setTotalInvestmentInput("10000"); 
    setVarResults(null); 
    setExampleLoaded(true); 
  };
  
  const handleResetInput = () => {
    setRows([{ ticker: "", weight: "" }]);
    setVarResults(null);
    setMessage("");
    setWhatIfChange({});
    navigate(location.pathname, { replace: true }); 
  };

  const handleTickerSelect = (index, ticker) => {
    handleChange(index, "ticker", ticker);
  };

  const handleShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.info(t("linkCopied"));
  };

  const calculatePortfolioMetrics = (skipUrlUpdateAndWeightCheck = false) => {
    setIsLoading(true);
    setVarResults(null); 

    if (validRows.length === 0) {
      alert(t('risk.alerts.noTickersOrWeights'));
      setIsLoading(false);
      return;
    }

    const totalWeightSum = validRows.reduce((acc, row) => acc + Number(row.weight || 0), 0);
    if (!skipUrlUpdateAndWeightCheck) {
        if (Math.abs(totalWeightSum - 100) > 0.01) {
          alert(t('risk.alerts.totalWeightNot100', { sum: totalWeightSum.toFixed(2) }));
          setIsLoading(false);
          return;
        }
    }
    
    const parsedTotalInvestment = Number(totalInvestmentInput);
    if (isNaN(parsedTotalInvestment) || parsedTotalInvestment <= 0) {
      alert(t('risk.alerts.invalidTotalInvestment'));
      setIsLoading(false);
      return;
    }
    setTotalInvestment(parsedTotalInvestment);
    setUsedConfidence(configConfidence);

    const weightsArr = validRows.map(row => Number(row.weight) / 100);
    const stocksArr = validRows.map(row => row.ticker);

    if (!skipUrlUpdateAndWeightCheck) {
        const stocks = stocksArr.join(','); 
        const weights = weightsArr.map(w => w.toFixed(4)).join(',');

        const newSearchParams = new URLSearchParams();
        newSearchParams.set('tickers', stocks);
        newSearchParams.set('weights', weights);
        newSearchParams.set('investment', parsedTotalInvestment.toString());
        newSearchParams.set('period', backtestPeriod.toString());
        newSearchParams.set('confidence', configConfidence.toString());
        newSearchParams.set('currency', currency);
        
        navigate(`?${newSearchParams.toString()}`, { replace: true });

        const manualWeights = validRows.reduce((acc, row) => {
            acc[row.ticker.toUpperCase()] = row.weight;
            return acc;
        }, {});
        localStorage.setItem("myStocks", JSON.stringify(stocksArr));
        localStorage.setItem("optimizedPortfolioWeights", JSON.stringify(manualWeights));
    }

    const encodedStocks = stocksArr.map(s => encodeURIComponent(s)).join(",");
    const encodedWeights = weightsArr.map(w => w.toString()).join(",");
    const url = `/.netlify/functions/var?stocks=${encodedStocks}&weights=${encodedWeights}&dataPeriod=${backtestPeriod}&confidence=${configConfidence}`;

    fetch(url)
      .then(res => {
        if (!res.ok) {
          return res.json().then(errorData => {
            throw new Error(errorData.message || t('risk.dataError'));
          });
        }
        return res.json();
      })
      .then(data => {
        setVarResults(data); 
        setDataPeriodInfo(data.dataPeriodInfo);
        setSelectedPeriod('Daily'); 
        setIsLoading(false); 
      })
      .catch(error => {
        if (error.message.includes("입력하신 종목들의 공통 거래일이 충분하지 않습니다")) {
          alert(error.message);
        } else {
          console.error("[Risk.js] API 호출 오류:", error);
        }
        setIsLoading(false);
      });
  };


  // --- 2.8 Render Logic (차트 데이터 및 계산) ---
  
  const currentPeriodData = getPeriodData(selectedPeriod);
  const { 
      portfolioStd: currentPortfolioStd, 
      varValue: currentVarValue, 
      historicalVaR: currentHistoricalVaR, 
      portfolioReturns: currentPortfolioReturns,
      marginalVaR: currentMarginalVaR,        
      riskContribution: currentRiskContribution,
      commonPeriod: currentCommonPeriod
  } = currentPeriodData;

  const FIVE_YEARS_IN_TRADING_DAYS = 1260; 
  const isPeriodTooShort = currentCommonPeriod.count < FIVE_YEARS_IN_TRADING_DAYS; 
  const commonPeriod = currentCommonPeriod;

  // 히스토그램 데이터 생성
  const histogram = getHistogramData(currentPortfolioReturns, 100);
  
  let cutoffBinIndex = null;
  if (histogram.binWidth > 0 && currentHistoricalVaR !== null) { 
    cutoffBinIndex = Math.floor((currentHistoricalVaR - histogram.min) / histogram.binWidth);
    if (cutoffBinIndex < 0) cutoffBinIndex = 0;
    if (cutoffBinIndex >= histogram.counts.length) {
      cutoffBinIndex = histogram.counts.length - 1;
    }
  }

  const barColors = histogram.counts.map((_, i) =>
    i <= cutoffBinIndex ? "rgba(255, 99, 132, 0.8)" : "rgba(75,192,192,0.4)"
  );

  const plotData = [
    {
      x: [null],
      y: [null],
      type: "bar",
      mode: "markers",
      name: t('risk.plot.upperHistorical', { confidence: (usedConfidence * 100).toFixed(0) }),
      marker: { color: "rgba(75,192,192,0.4)" },
    },
    {
      x: histogram.labels.map(label => (parseFloat(label) * 100).toFixed(2) + "%"),
      y: histogram.counts,
      type: "bar",
      name: t('risk.plot.lowerHistorical', { confidenceComplement: ((1 - usedConfidence) * 100).toFixed(0) }),
      marker: { color: barColors },
      showlegend: true,
    }
  ];

  if (currentPortfolioReturns.length > 0 && currentPortfolioStd !== null) { 
    const mu = currentPortfolioReturns.reduce((acc, cur) => acc + cur, 0) / currentPortfolioReturns.length;
    const sigma = currentPortfolioStd; 
    
    const normalPDF = (x, m, s) =>
      (1 / (s * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * Math.pow((x - m) / s, 2));
    
    const binCenters = histogram.labels.map(label => parseFloat(label) + histogram.binWidth / 2);
    
    const normalData = binCenters.map(center => {
      const pdfValue = normalPDF(center, mu, sigma);
      return pdfValue * currentPortfolioReturns.length * histogram.binWidth;
    });

    const z = getZValue(usedConfidence);
    const parametricCutoff = mu - z * sigma;
    
    const tailData = normalData.map((val, i) => {
      const xVal = binCenters[i];
      return xVal <= parametricCutoff ? val : null;
    });

    plotData.push({
      x: histogram.labels.map(label => (parseFloat(label) * 100).toFixed(2) + "%"),
      y: normalData,
      type: "scatter",
      mode: "lines",
      name: t('risk.plot.upperParametric', { confidence: (usedConfidence * 100).toFixed(0) }),
      line: { color: "rgba(0, 0, 0, 1)" },
    });

    plotData.push({
      x: histogram.labels.map(label => (parseFloat(label) * 100).toFixed(2) + "%"),
      y: tailData,
      type: "scatter",
      mode: "lines",
      name: t('risk.plot.lowerParametric', { confidenceComplement: ((1 - usedConfidence) * 100).toFixed(0) }),
      line: { color: "rgba(255, 0, 0, 0.8)" },
    });
  }

  const periodXAxisTitle = {
      'Daily': t('risk.chart.xaxis.daily', '하루 수익률'),
      'Weekly': t('risk.chart.xaxis.weekly', '주별 롤링 수익률'),
      'Monthly': t('risk.chart.xaxis.monthly', '월별 롤링 수익률'),
      'Quarterly': t('risk.chart.xaxis.quarterly', '분기별 롤링 수익률'),
      'Annual': t('risk.chart.xaxis.annual', '연별 롤링 수익률'),
  }[selectedPeriod];

  const periodChartTitle = {
    'Daily': t('risk.chart.title.daily', '일별 수익률 분포'),
    'Weekly': t('risk.chart.title.weekly', '주별 수익률 분포'),
    'Monthly': t('risk.chart.title.monthly', '월별 수익률 분포'),
    'Quarterly': t('risk.chart.title.quarterly', '분기별 수익률 분포'), 
    'Annual': t('risk.chart.title.annual', '연별수익률 분포'), 
  }[selectedPeriod];

  const layout = {
    title: periodXAxisTitle,
    xaxis: {
      title: { text: periodXAxisTitle },
      tickformat: ".2f%",
    },
    yaxis: { title: { text: t('risk.plot.yaxis') }, rangemode: "tozero" },
    autosize: true,
    legend: { x: 1, xanchor: "right", y: 1 },
    margin: { l: 80, r: 50, t: 50, b: 80 },
    dragmode: false, 
    shapes: [
        {
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: '0',
            x1: '0',
            y0: 0,
            y1: 1,
            line: {
                color: 'rgb(50, 50, 50)', 
                width: 2, 
                dash: 'dot' 
            }
        }
    ]
  };

  const config = {
    responsive: true,
    displayModeBar: false,
    scrollZoom: false,
    displaylogo: false,
  };

  const historicalLossRate = currentHistoricalVaR !== null && currentHistoricalVaR < 0 
  ? -currentHistoricalVaR 
  : 0; 

  const historicalCutoffPercentage = historicalLossRate * 100;
  const historicalCutoffMoney = totalInvestment * historicalLossRate;
  
  const lossRate = currentVarValue !== null && currentVarValue < 0 
  ? -currentVarValue 
  : 0;

  const parametricVarPercentage = lossRate * 100;
  const parametricVarMoney = totalInvestment * lossRate;

  // -----------------------------------------------------------------
  // ✨ [추가] What-if 총합 계산 로직 (Render Logic 내부, return 문 직전)
  // -----------------------------------------------------------------
  let totalWhatIfWeightChange = 0;
  let totalWhatIfVarChangeBp = 0;
  let totalWhatIfVarChangeMoney = 0; // 금액 변화 추가

  if (currentMarginalVaR && validRows.length > 0) {
      validRows.forEach((row, index) => {
          const storedValue = whatIfChange[row.ticker];
          if (storedValue) {
              const val = Number(storedValue);
              if (!isNaN(val)) {
                  // 1. 비중 변화 합계 (%)
                  totalWhatIfWeightChange += val;

                  const mVaR = currentMarginalVaR[index];
                  const changeInWeight = val / 100;
                  
                  // 2. VaR 변화 합계 (bp)
                  totalWhatIfVarChangeBp += (mVaR * changeInWeight * 10000); 
                  
                  // 3. VaR 변화 합계 (금액)
                  totalWhatIfVarChangeMoney += (mVaR * changeInWeight * totalInvestment); 
              }
          }
      });
  }

  const stocks = validRows.map(row => row.ticker);
  const optimizedWeights = validRows.reduce((acc, row) => {
      acc[row.ticker] = row.weight;
      return acc;
  }, {});
  
  // --- 2.9 Render (JSX) ---
  return (
    <div className={styles.VaRpage}>
      <main className={commonStyles.Container}>
        {/* === 인트로 === */}
        <div className={commonStyles.introContainer}>
          <p className={commonStyles.introMain}>{t('risk.intro')}</p>
          <p className={commonStyles.introSub}>{t("risk.introSub")}</p>
        </div>

        {/* === 상단 섹션 === */}
        <section className={styles["top-section"]}>
          
          {/* --- 1. 설정 카드 --- */}
          <div className={`${styles.card} ${styles["settings-card"]}`}>
            <h3>{t('risk.settings.title')}</h3>
            <div className={styles["setting-row"]}>
              <label className={styles["label"]}>
                {t('risk.settings.dataPeriod')} {backtestPeriod}{t('risk.settings.year')}
              </label>
              <input
                type="range"
                min="1"
                max="30"
                step="1"
                value={backtestPeriod}
                onChange={(e) => setBacktestPeriod(parseInt(e.target.value, 10))}
              />
            </div>
            <div className={styles["setting-row"]}>
              <label className={styles["label"]}>
                {t('risk.settings.confidence')} {(configConfidence * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min="90"
                max="99"
                step="1"
                value={configConfidence * 100}
                onChange={(e) => setConfigConfidence(parseFloat(e.target.value) / 100)}
              />
            </div>
          </div>

          {/* --- 2. 포트폴리오 입력 카드 --- */}
          <div className={`${styles.card} ${styles["portfolio-card"]}`}>
            <h3>{t('risk.portfolioInput.title')}</h3>
            
            {/* 통화 및 총 투자금 */}
            <div className={styles["setting-row2"]}>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                <option value="KRW">KRW (₩)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>
            <div className={styles["setting-row3"]}>
                <label>{t('risk.portfolioInput.totalInvestment')}</label>
                <NumericFormat
                    value={totalInvestmentInput}
                    thousandSeparator={true}
                    allowNegative={false}
                    placeholder={t('risk.placeholder.totalInvestment', { currency: getCurrencySymbol(currency) })}
                    onValueChange={(values) => setTotalInvestmentInput(values.value)}
                    className={`${styles.inputValue} `}
                />
            </div>

            <p className={styles["instruction"]}>{t('risk.portfolioInput.instruction')}</p>
            
            {/* 종목 입력 행 */}
            <div className={styles["input-section2"]}>
              {rows.map((row, index) => (
                <div key={index} className={`${styles["row-line"]} ${styles["white-box"]}`}>
                  <>
                    <TickerRow
                      value={row.ticker}
                      disableDefaultStyles={false}
                      onChange={(newTicker) => handleTickerSelect(index, newTicker)}
                      className={styles.inputTicker}
                      existingTickers={rows.map(row => row.ticker).filter(ticker => ticker && ticker.trim() !== "")}
                    />
                    <NumericFormat
                      value={row.weight}
                      thousandSeparator={true}
                      allowNegative={false}
                      decimalScale={2}
                      suffix="%"
                      placeholder={t('risk.portfolioInput.weightPlaceholder')}
                      onValueChange={(values) => handleChange(index, "weight", values.value)}
                      className={styles.inputValue}
                    />
                    <button type="button" onClick={() => handleRemoveRow(index)}>
                      x
                    </button>
                  </>
                </div>
              ))}
            </div>

            {/* 총 비중 */}
            <div className={styles["weight-sum-display"]}>
              <span className={styles["label"]}>{t('risk.portfolioInput.totalWeight')}: </span>
              <span className={getWeightSumColor(currentWeightSum)}>
                {currentWeightSum.toFixed(2)}%
              </span>
            </div>

            {/* 버튼 그룹들 (예시로드, 추가, 초기화, 불러오기, 저장, 계산) */}
            <div className={styles["button-group"]}>
                <button 
                  className={styles["add-button"]} 
                  type="button" 
                  onClick={handleLoadExample} 
                  style={{ backgroundColor: '#2b9881' }}
                  disabled={isLoading}
                >
                  {t('backtest.loadExamplesButton') || '예시 포트폴리오 로드'}
                </button>
            </div>
            <br></br>
            <div className={styles["button-group"]} style={{  justifyContent: 'space-between' }}>
               <button 
                className={styles["add-button"]} 
                type="button" 
                onClick={handleAddRow}
                disabled={isLoading}
              >
                {t('risk.portfolioInput.addTicker')}
              </button>
                
                <button 
                  className={styles["add-button"]} 
                  type="button" 
                  onClick={handleResetInput} 
                  style={{ backgroundColor: '#dc3545'}} 
                  disabled={isLoading}
                >
                  {t('backtest.resetButton') || '초기화'}
                </button>
            </div>
            <br></br>
            <div className={styles["button-group"]} style={{ marginTop: '10px', justifyContent: 'space-between' }}>
              <button
                onClick={() => actions.fetchUserPortfolios(false)} 
                className={styles["add-button"]} 
                disabled={actions.isPortfoliosLoading || isLoading}
                style={{ 
                  backgroundColor: actions.isPortfoliosLoading ? '#adb5cd' : (actions.isLoadModalOpen ? '#dc3545' : '#6c757d'), 
                  color: 'white', opacity: actions.isPortfoliosLoading ? 0.7 : 1 
                }}
                title={!auth.isAuthenticated ? t('newPortfolioModal.loginRequiredTooltip') || '로그인이 필요합니다.' : ''}
              >
                {actions.isPortfoliosLoading ? t('backtest.loading') || '로딩 중...' : (actions.isLoadModalOpen ? (t('backtest.close') || "닫기") : (t('backtest.loadMyPortfolioButton') || "내 포트폴리오 불러오기"))}
              </button>
              <button 
                onClick={actions.handleOpenSaveModal} 
                className={styles["calculate-button"]} 
                disabled={actions.isPortfoliosLoading || isLoading || setup.validRows.length === 0 || Math.abs(setup.totalWeight - 100) > 0.1}
                style={{ backgroundColor: '#007bff' }} 
                title={!auth.isAuthenticated ? t('newPortfolioModal.loginRequiredTooltip') || '로그인이 필요합니다.' : ''}
              >
                {t('backtest.savePortfolioButton') || "포트폴리오 저장"}
              </button>
            </div>

            {/* 불러오기 드롭다운 */}
            {actions.isLoadModalOpen && actions.userPortfolios.length > 0 && (
              <div className={styles.portfolioDropdown} style={{ marginTop: '10px', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '1em', fontWeight: 'bold' }}>{t('backtest.selectPortfolio') || '포트폴리오를 선택하세요.'}</h3>
                <ul style={{ listStyle: 'none', padding: 0, maxHeight: '200px', overflowY: 'auto' }}>
                  {actions.userPortfolios.map(p => {
                    const isAll = p.portfolioType === 'ALL';
                    return (
                      <li key={p.portfolioId} className={styles.portfolioListItem} 
                      style={{ 
                        fontWeight: isAll ? 'bold' : 'normal', 
                        backgroundColor: isAll ? '#e9f7ff' : '#f8f9fa', 
                        color: isAll ? '#007bff' : '#343a40',
                        padding: '8px',
                        marginBottom: '5px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}>
                        <span onClick={() => handleLoadPortfolioCustom(p)} style={{ flexGrow: 1 }}>{p.name}</span>
                        {p.portfolioType === 'PRIVATE' && (
                          <button onClick={(e) => { 
                            e.stopPropagation(); 
                            actions.handleStartTrackingPortfolio(p); 
                          }} disabled={actions.isPortfoliosLoading} style={{ 
                            padding: '5px 10px', fontSize: '0.8em', 
                            backgroundColor: '#28a745', color: 'white', 
                            border: 'none', borderRadius: '4px', marginLeft: '10px' 
                          }}>
                            {t('backtest.startTracking') || '성과 추적 시작'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <br></br>
            <button 
                className={styles["calculate-button"]} 
                type="button" onClick={() => calculatePortfolioMetrics(false)}
                disabled={isLoading}
              >
                {isLoading ? (
                    <div className={commonStyles.smallRowLoadingContainer} style={{justifyContent: 'center', margin: 0 }}>
                      <div className={commonStyles.spinner} style={{ width: '1em', height: '1em', marginRight: '5px' }}></div>
                    </div>
                  ) : (
                    t('risk.portfolioInput.calculate')
                  )}
            </button>
          </div>
        </section>
        
        {/* === 공유 버튼 === */}
        {(varResults) && (
          <button
            onClick={handleShareLink}
            className={styles.calculateButton} 
            style={{ marginTop: '10px', marginBottom: '10px', backgroundColor: '#5cb85c' }} 
          >
            {t("shareLink")}
          </button>
        )}

        {/* === 데이터 기간 경고 === */}
        {varResults && dataPeriodInfo && dataPeriodInfo.isPeriodLimited && (
          <DataPeriodAlert 
              dataPeriodInfo={dataPeriodInfo} 
              t={t}
          />
        )}
        
          {varResults && (
          <div className={commonStyles["data-info-box"]}>
              <p>
                  <strong>{t('common.period.used')}: </strong>
                  {commonPeriod.start} ~ {commonPeriod.end}
              </p>
              <p>
                  <strong>{t('common.days.total')}: </strong>
                  {commonPeriod.count} {t('common.days.unit')} 
              </p>
              {isPeriodTooShort ? (
                  <p>
                      <strong className={commonStyles.warningText}> 
                          {t('common.period.short_period_warning') || '⚠️ 분석 기간이 5년 미만이므로 시뮬레이션 결과의 신뢰도가 낮을 수 있습니다.'} 
                      </strong>
                  </p>
              ) : (
                  <p>
                      <strong color="red"> 
                          {t('common.period.min_years_recommend') || '최소 5년 이상 권장'} 
                      </strong>
                  </p>
              )}
          </div>    
        )}


        {/* === 기간 선택 버튼 === */}
        {varResults && (
            <div className={styles.periodSelector}> 
                {[{ key: 'Daily', label: t('risk.period.daily', '1일 (일별)') }, 
                 { key: 'Weekly', label: t('risk.period.weekly', '5일 (주별 롤링)') }, 
                 { key: 'Monthly', label: t('risk.period.monthly', '21일 (월별 롤링)') },
                 { key: 'Quarterly', label: t('risk.period.quarterly', '63일 (분기별 롤링)') }, 
                 { key: 'Annual', label: t('risk.period.annual', '252일 (1년 롤링)') }
                ].map(period => (
                    <button
                        key={period.key}
                        onClick={() => setSelectedPeriod(period.key)}
                        className={selectedPeriod === period.key ? styles.active : ''} 
                        style={{
                            display: varResults[`VaR_${period.key}`] === null ? 'none' : 'inline-block' 
                        }}
                    >
                        {period.label}
                    </button>
                ))}
            </div>
        )}

        {/* === 결과 섹션 (VaR 값) === */}
        {varResults && currentPeriodData.varValue !== null && (
            <section ref={resultsRef} className={styles["results-section"]}>
              <div className={styles["result-container"]}>
                
                {/* Historical VaR */}
                <div className={styles["result-box"]}>
                  <h3>{t('risk.historicalVaR.title')}</h3>
                  <div className={styles["circular-chart"]}>
                      <div className={styles.circle} style={{ "--percentage": historicalCutoffPercentage }}>
                      <span className={styles["percentage-value"]}>
                          {formatNumber(historicalCutoffMoney)} {getCurrencySymbol(currency)}
                      </span>
                      <span className={styles["loss-percentage"]}>
                          {historicalCutoffPercentage.toFixed(2)}%
                      </span>
                      </div>
                  </div>
                   <p className={styles["sub-text"]}>
                    {t('risk.historicalVaR.description', {
                        period: t(`risk.period.${
                            {
                                'Daily': 'd',
                                'Weekly': 'w',
                                'Monthly': 'm',
                                'Quarterly': 'q',
                                'Annual': 'a'
                            }[selectedPeriod]
                        }`), 
                        percent: ((1 - usedConfidence) * 100).toFixed(0),
                        amount: formatNumber(historicalCutoffMoney),
                        currency: getCurrencySymbol(currency)
                    })}
                  </p>
                </div>

                {/* Parametric VaR */}
                <div className={styles["result-box"]}>
                  <h3>{t('risk.parametricVaR.title')}</h3>
                  <div className={styles["circular-chart"]}>
                      <div className={styles.circle} style={{ "--percentage": parametricVarPercentage }}>
                      <span className={styles["percentage-value"]}>
                          {formatNumber(parametricVarMoney)} {getCurrencySymbol(currency)}
                      </span>
                      <span className={styles["loss-percentage"]}>
                      {parametricVarPercentage.toFixed(2)}%
                      </span>
                      </div>
                  </div>
                  <p className={styles["sub-text"]}>
                    {t('risk.parametricVaR.description', {
                        period: t(`risk.period.${
                            {
                                'Daily': 'd',
                                'Weekly': 'w',
                                'Monthly': 'm',
                                'Quarterly': 'q',
                                'Annual': 'a'
                            }[selectedPeriod]
                        }`), 
                        percent: ((1 - usedConfidence) * 100).toFixed(0),
                        amount: formatNumber(parametricVarMoney),
                        currency: getCurrencySymbol(currency)
                    })}
                  </p>
                </div>
              </div>
            </section>
        )}

        
        {/* === 차트 섹션 (히스토그램) === */}
        {currentPortfolioReturns.length > 0 && (
          <div className={styles["chart-section"]}>
            <h2>
              {periodChartTitle} <br />
              <span className={styles["sub-title"]}>
                {t('risk.chart.subtitle', { start: currentCommonPeriod.start, end: currentCommonPeriod.end })}
              </span>
            </h2>
            <div className={styles["chart-scroll"]}>
              <div className={styles["chart-container"]}>
                <Plot
                  key={selectedPeriod} 
                  data={plotData}
                  layout={layout}
                  config={config}
                  useResizeHandler={true}
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* === 1. Risk Contribution Pie Chart (위험 기여도 파이 차트) === */}
        {currentRiskContribution && currentRiskContribution.length > 0 && validRows.length > 0 && (() => {
            // 1. 데이터 결합 및 정렬 로직 (기여도 내림차순)
            const sortedRiskData = validRows.map((row, index) => ({
                ticker: row.ticker,
                value: currentRiskContribution[index]
            })).sort((a, b) => b.value - a.value); // 내림차순 정렬 (큰 값이 위로, 음수는 아래로)

            // 2. 차트 표시를 위한 값 계산
            // ⭐️ X축 표시 값: 항상 양수(절대값)를 사용하여 막대를 오른쪽으로 그림
            const displayXValues = sortedRiskData.map(d => Math.abs(d.value * 100));
            // 텍스트 레이블: 원래의 값(부호 포함)
            const textLabels = sortedRiskData.map(d => (d.value * 100).toFixed(1) + '%');
            // 색상 결정: 원래의 값 기준 (양수=빨강, 음수=녹색)
            const colors = sortedRiskData.map(d => d.value >= 0 ? 'rgba(255, 99, 132, 0.7)' : 'rgba(40, 167, 69, 0.7)');
            const lineColors = sortedRiskData.map(d => d.value >= 0 ? 'rgba(255, 99, 132, 1)' : 'rgba(40, 167, 69, 1)');
            const textColors = sortedRiskData.map(d => d.value >= 0 ? 'rgba(255, 99, 132, 1)' : 'rgba(40, 167, 69, 1)');
            

            return (
                <div className={styles["chart-section"]}>
                    <h2>{t('risk.riskContribution.title', '종목별 위험 기여도')}</h2>
                    <span className={styles["sub-title"]}>
                        {t('risk.riskContribution.subtitle', '포트폴리오 총 VaR에 대한 각 종목의 기여도 (음수는 위험 분산 효과)')}
                    </span>
                    <div className={styles["chart-scroll"]}>
                        <div className={styles["chart-container"]}>
                            <Plot
                                data={[
                                    // Trace 1: Bar Chart (막대 시각화) - 항상 양수 방향으로
                                    {
                                        // ⭐️ 항상 양수 방향의 절대값 사용
                                        x: displayXValues,
                                        y: sortedRiskData.map(d => d.ticker),
                                        type: 'bar',
                                        orientation: 'h',
                                        showlegend: false,
                                        
                                        marker: {
                                            // ⭐️ 색상 로직은 원래의 값(value) 기준
                                            color: colors,
                                            line: {
                                                color: lineColors,
                                                width: 1
                                            }
                                        },
                                        // 호버 시 부호가 있는 실제 값을 표시하기 위해 customdata 사용
                                        customdata: textLabels,
                                        hovertemplate: "%{y}<br>기여도: %{customdata}<extra></extra>"
                                    },
                                    // ⭐️ Trace 2: Scatter Trace for Text Labels (텍스트 레이블) - 막대 끝에 배치
                                    {
                                        type: 'scatter',
                                        mode: 'text',
                                        x: displayXValues.map(x => x * 1.05), 
                                        y: sortedRiskData.map(d => d.ticker),
                                        text: textLabels,
                                        
                                        textposition: 'middle right', 
                                        // textposition: 'auto',
                                        showlegend: false,
                                        hoverinfo: 'none', 
                                        textfont: {
                                            color: textColors,
                                            size: 11
                                        }
                                    }
                                ]}
                                layout={{
                                    title: { 
                                        text: `${selectedPeriod} ${t('risk.riskContribution.chartTitle', '위험 기여도')}`,
                                        font: { size: 16 } 
                                    },
                                    height: 450,
                                    margin: { t: 50, b: 50, l: 100, r: 50 }, 
                                    xaxis: {
                                        // ⭐️ X축 제목을 절대값으로 변경하여 혼동 방지
                                       
                                        title: { text: t('risk.riskContribution_absolute', '기여도 절대값 (%)') }, 
                                        
                                        zeroline: true,
                                        zerolinewidth: 2,
                                        zerolinecolor: '#000',
                                        // X축 범위가 0부터 시작하도록 강제
                                        rangemode: 'tozero' ,
                                    },
                                    yaxis: {
                                        automargin: true,
                                        autorange: 'reversed'
                                    },
                                    barmode: 'group',
                                    dragmode: false,
                                }}
                                config={config}
                                // config={{
                                //   ...config, 
                                //   staticPlot: true
                                // }}
                                useResizeHandler={true}
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                    </div>
                </div>
            );
        })()}


        {/* === 2. Marginal VaR 테이블 === */}
        {(currentMarginalVaR && currentMarginalVaR.length > 0 && validRows.length > 0 && currentRiskContribution) && (() => {
            
            // 1. 데이터 결합 및 정렬 (차트와 동일한 기준: Risk Contribution 내림차순)
            const sortedTableData = validRows.map((row, index) => ({
                row: row,
                mVaR: currentMarginalVaR[index],
                riskContrib: currentRiskContribution[index] || 0 // 정렬 기준
            })).sort((a, b) => b.riskContrib - a.riskContrib);

            return (
                // <div className={styles["table-section"]}>
                <div className={styles["chart-section"]}>
                    <h2>{t('risk.marginalVaR.title', '손실 기여도 분석')}</h2>
                    <span className={styles["sub-title"]}>
                        {t('risk.marginalVaR.subtitle', '특정 종목의 비중을 조절했을 때 포트폴리오 총 위험의 예상 변화량')}
                    </span>
                    
                    <div className={styles.responsiveTableContainer}>
                        <table className={styles.varTable}>
                            <thead>
                                <tr>
                                    <th>{t('risk.ticker', '종목')}</th>
                                    <th>{t('risk.currentWeight', '현재 비중')}</th>
                                    <th>{t('risk.marginalVaR.recommendation', '위험 감소 경로')}</th>
                                    <th>{t('risk.marginalVaR.change1pct', '1% 비중 축소 시 위험 변화')}</th>
                                    <th className={styles.whatifDivider}>
                                        {t('risk.whatIf.change', '비중 변화 시뮬레이션')}
                                    </th>
                                    <th>{t('risk.whatIf.result', '예상 VaR 변화')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTableData.map((item) => {
                                    // 정렬된 item에서 데이터 추출
                                    const row = item.row;
                                    const mVaR = item.mVaR;
                                    // (riskContrib는 정렬용으로만 사용하고 표시는 안 함)
                                    const weight = Number(row.weight);
                                    const changeFor1pctDecrease = mVaR * -0.01; 
                                    let isReduceRecommended = true; 
                                    let recommendation = t('risk.marginalVaR.recommend.reduce', '축소');
                                    let explanation = t('risk.marginalVaR.explain.reduce', '총 위험을 줄일 수 있음');
                                    let icon = '🔻'; 

                                    if (changeFor1pctDecrease > 0) { 
                                            isReduceRecommended = false;
                                            recommendation = t('risk.marginalVaR.recommend.maintain', '확대');
                                            explanation = t('risk.marginalVaR.explain.maintain', '위험 분산 효과 있음');
                                            icon = '⬆️'; 
                                    } else if (Math.abs(changeFor1pctDecrease * 10000) < 0.01) {
                                            isReduceRecommended = true; 
                                            recommendation = t('risk.marginalVaR.recommend.neutral', '유지');
                                            explanation = t('risk.marginalVaR.explain.neutral', '영향이 미미함');
                                            icon = '➖'; 
                                    }

                                    const storedValue = whatIfChange[row.ticker];
                                    let isNegative;
                                    if (storedValue !== undefined && storedValue !== '') {
                                        isNegative = Number(storedValue) < 0;
                                    } else {
                                        isNegative = isReduceRecommended;
                                    }
                                    const isMatch = (isReduceRecommended && isNegative) || (!isReduceRecommended && !isNegative);
                                    const displayValue = storedValue ? Math.abs(Number(storedValue)) : '';

                                    // [Line 1133]
                                    let whatIfResultBp = null;   // What-if VaR 변화 (bp)
                                    let whatIfResultMoney = null; // What-if VaR 변화 (금액)

                                    if (storedValue) {
                                        const changeInWeight = Number(storedValue) / 100;

                                        // 1. VaR 변화 (bp) = mVaR * (비중변화%/100) * 10000
                                        whatIfResultBp = mVaR * changeInWeight * 10000; 
                                        
                                        // 2. VaR 변화 (비율) = mVaR * (비중변화%/100)
                                        const changeInVaRRate = mVaR * changeInWeight; 
                                        
                                        // 3. VaR 변화 (금액) = VaR 변화 (비율) * 총 투자 금액
                                        whatIfResultMoney = changeInVaRRate * totalInvestment;
                                    }

                                    return (
                                        <tr key={row.ticker}>
                                            <td data-label={t('common.ticker', '종목')}>{row.ticker}</td>
                                            <td data-label={t('common.currentWeight', '현재 비중')} className={styles.textRight}>
                                                {weight.toFixed(2)}%
                                            </td>
                                   
                                            <td data-label={t('risk.marginalVaR.reductionPath', '위험 감소 경로')}>
                                                <span className={styles.recommendationBox}>
                                                    <span>{icon}</span>
                                                    <strong>{recommendation}</strong>
                                                </span>
                                                <span className={styles.explanationText}>({explanation})</span>
                                            </td>
                                            <td data-label={t('risk.marginalVaR.change1pct', '1% 축소 시 변화')} className={changeFor1pctDecrease > 0 ? styles.redText : styles.greenText}>
                                                <NumericFormat value={changeFor1pctDecrease * 10000} displayType="text" decimalScale={2} fixedDecimalScale={true} suffix="bp" prefix={changeFor1pctDecrease > 0 ? '+' : ''} thousandSeparator={true} />
                                            </td>
                                       
                                            <td data-label={t('risk.whatIf.change', '비중 변화')} className={styles.whatifCell}>
                                                <div className={styles.whatIfContainer}>
                                                    <button 
                                                        className={`${styles.signButton} ${isNegative ? styles.minus : styles.plus} ${isMatch ? styles.match : styles.mismatch}`}
                                                        onClick={() => {
                                                            if (!storedValue) { handleWhatIfChange(row.ticker, 1, !isNegative, weight); } 
                                                            else { handleWhatIfChange(row.ticker, displayValue, !isNegative, weight); }
                                                        }}
                                                    >
                                                        {isNegative ? '－' : '＋'}
                                                    </button>
                                                    <input 
                                                        type="number" className={styles.whatIfInput} value={displayValue}
                                                        onChange={(e) => handleWhatIfChange(row.ticker, e.target.value, isNegative, weight)}
                                                        placeholder="0" min="0" max={isNegative ? weight : 100}
                                                    />
                                                    <span style={{ fontSize: '0.9em', color: '#666', minWidth: '15px' }}>%</span>
                                                </div>
                                                {isNegative && Number(displayValue) === weight && (
                                                    <div className={styles.maxReduceMessage}>{t('risk.max_minimize', '최대 축소')}  </div>
                                                )}
                                            </td>
                                            <td data-label={t('risk.whatIf.result', '예상 결과')} className={whatIfResultBp > 0 ? styles.redText : (whatIfResultBp < 0 ? styles.greenText : '')}>
                                                {whatIfResultBp !== null ? (
                                                    <>
                                                        <NumericFormat 
                                                            value={whatIfResultBp} 
                                                            displayType="text" 
                                                            decimalScale={2} 
                                                            fixedDecimalScale={true} 
                                                            suffix=" bp" 
                                                            prefix={whatIfResultBp > 0 ? '+' : ''} 
                                                            thousandSeparator={true} 
                                                        />
                                                        {/* ⭐️ 추가된 금액 변화 표시 ⭐️ */}
                                                        {whatIfResultMoney !== null && (
                                                            <span className={styles.moneyChange} style={{ display: 'block', fontSize: '0.9em' }}>
                                                                ({whatIfResultMoney > 0 ? '+' : ''}
                                                                <NumericFormat 
                                                                    value={whatIfResultMoney} 
                                                                    displayType="text" 
                                                                    decimalScale={0} 
                                                                    fixedDecimalScale={false} 
                                                                    thousandSeparator={true} 
                                                                /> {getCurrencySymbol(currency)})
                                                            </span>
                                                        )}
                                                    </>
                                                ) : <span className={styles.placeholderText}>-</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className={styles.tableFooter}>
                                <tr className={styles.footerSummaryRow}>
                                    <td className={styles.totalImpactLabel} >
                                      {t('risk.total', '합계')}
                                    </td>
                                    <td className={styles.desktopOnly}></td>
                                    <td className={styles.desktopOnly}></td>
                                    <td className={styles.desktopOnly}></td>
                                    
                                    <td className={`${styles.whatifCell} ${styles.textRight}`} data-label="총 비중 변화">
                                        <span>
                                            {totalWhatIfWeightChange > 0 ? '+' : ''}{totalWhatIfWeightChange.toFixed(2)}%
                                        </span>
                                    </td>
                                    <td className={totalWhatIfVarChangeBp > 0 ? styles.redText : (totalWhatIfVarChangeBp < 0 ? styles.greenText : '')} data-label="총 위험 변화">
                                        <NumericFormat value={totalWhatIfVarChangeBp} displayType="text" decimalScale={2} fixedDecimalScale={true} suffix=" bp" prefix={totalWhatIfVarChangeBp > 0 ? '+' : ''} thousandSeparator={true} />
                                        
                                        {/* ⭐️ 추가된 총합 금액 변화 표시 ⭐️ */}
                                        <span className={styles.moneyChange} style={{ display: 'block', fontSize: '0.9em' }}>
                                            ({totalWhatIfVarChangeMoney > 0 ? '+' : ''}
                                            <NumericFormat 
                                                value={totalWhatIfVarChangeMoney} 
                                                displayType="text" 
                                                decimalScale={0} 
                                                fixedDecimalScale={false} 
                                                thousandSeparator={true} 
                                            /> {getCurrencySymbol(currency)})
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                  <td colSpan={6} className={styles.footerNote}>
                                      {t('risk.sim_assumption', '💡 전체 비중 축소는 현금 전환, 증가는 차입 가정. (100bp = 1%)')} 
                                  </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            );
        })()}

        {/* === 경쟁 등록 버튼 === */}
        {(varResults) && (
          <ChallengeCta stocks={stocks} optimizedWeights={optimizedWeights} />
        )}

      
        {/* === 공유 버튼 === */}
        {(varResults) && (
          <button
            onClick={handleShareLink}
            className={styles.calculateButton} 
            style={{ marginTop: '10px', marginBottom: '10px', backgroundColor: '#5cb85c' }} 
          >
            {t("shareLink")}
          </button>
        )}
        

        {/* === 포트폴리오 저장 모달 === */}
        {actions.showSaveModal && (
          <NewPortfolioModal
            onClose={() => actions.setShowSaveModal(false)}
            onCreated={actions.handlePortfolioSaved} 
            initialHoldings={actions.tempHoldings} 
            initialName={''} 
            isForcedPublic={false}
          />
        )}
      </main>

      {/* === 토스트 알림 컨테이너 === */}
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />  
    </div>
  );
}

export default Risk;